import React from 'react';
import { Typography, Grid, useMediaQuery, useTheme } from '@mui/material';
import {
  FilterAccordion,
  FilterAccordionSummary,
  FilterAccordionDetails,
  Divider
} from '../styledComponents';
import { ExpandMoreIcon } from './ExpandMoreIcon';

import styled from '@emotion/styled';

const GenericButton = styled.button`
  min-width: 48px;
  height: 42px;
  border-radius: 10px;
  padding: 8px 16px;
  text-transform: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Prompt', sans-serif;
  transition: all 0.2s ease;
  border: 0px solid #d3d3d3;
  background-color: white;
  color: #666666;
  display: block;
  text-align: left;
  width: 100%;
  margin-bottom: 8px;
  
  &:hover {
    color: #F4762A;
  }
  
  &.selected {
    color: #F4762A;
  }
`;

export interface FilterOption {
  id: number;
  name: string;
  name_th: string;
  name_en: string;
  name_cn: string;
  query_params: Record<string, any>;
  query_string: string;
  order: string | number;
}

export interface FilterGroup {
  group_id: number;
  group_name: string;
  group_name_th: string;
  group_name_en: string;
  group_name_cn: string;
  order: number;
  options: FilterOption[];
}

const getLocalizedName = (
  item: FilterGroup | FilterOption, 
  lang: string = 'th'
): string => {
  const name_th = 'group_name_th' in item ? item.group_name_th : item.name_th;
  const name_en = 'group_name_en' in item ? item.group_name_en : item.name_en;
  const name_cn = 'group_name_cn' in item ? item.group_name_cn : item.name_cn;
  const name = 'group_name' in item ? item.group_name : item.name;
  const itemId = 'group_id' in item ? item.group_id : item.id;
  
  let result = '';
  
  if (lang === 'en') {
    result = name_en || name_th || name || `[Missing EN name for ID: ${itemId}]`;
  } else if (lang === 'cn') {
    result = name_cn || name_th || name || `[Missing CN name for ID: ${itemId}]`;
  } else {
    result = name_th || name || `[Missing TH name for ID: ${itemId}]`;
  }
  
  return result;
};

interface GenericFilterSectionProps {
  filterGroup: FilterGroup;
  isExpanded: boolean;
  isVisible: boolean;
  onToggle: (event: React.MouseEvent) => void;
  selectedValue?: string | null;
  onValueSelect?: (value: string | null, option: FilterOption) => void;
  layout?: 'single' | 'double'; // single column หรือ double column
  lang?: string; // เพิ่ม lang parameter
  onCollapseAfterSelect?: () => void; // callback สำหรับหุบ accordion หลังเลือก
}

export const GenericFilterSection: React.FC<GenericFilterSectionProps> = ({
  filterGroup,
  isExpanded,
  isVisible,
  onToggle,
  selectedValue,
  onValueSelect,
  layout = 'double',
  lang = 'th', // default เป็นภาษาไทย
  onCollapseAfterSelect
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  
  if (!isVisible) return null;

  const handleOptionSelect = (option: FilterOption) => {
    const newValue = selectedValue === option.id.toString() ? null : option.id.toString();
    onValueSelect?.(newValue, option);
    
    // ใน mobile ถ้าเลือก option แล้วให้หุบ accordion
    if (isMobile && newValue !== null && onCollapseAfterSelect) {
      setTimeout(() => {
        onCollapseAfterSelect();
      }, 100); // delay เล็กน้อยเพื่อให้ UI update ก่อน
    }
  };

  const shouldUseSingleColumn = filterGroup.options.length < 6;
  const finalLayout = shouldUseSingleColumn ? 'single' : layout;

  const optionsLeft = finalLayout === 'double' 
    ? filterGroup.options.filter((_, index) => index % 2 === 0)
    : filterGroup.options;
  
  const optionsRight = finalLayout === 'double'
    ? filterGroup.options.filter((_, index) => index % 2 === 1)
    : [];

  // หาค่าที่เลือกเพื่อแสดงในหัวข้อ
  const selectedOption = filterGroup.options.find(option => option.id.toString() === selectedValue);
  const selectedOptionName = selectedOption ? getLocalizedName(selectedOption, lang) : null;
  
  const groupName = getLocalizedName(filterGroup, lang);

  return (
    <>
      <FilterAccordion className={isExpanded ? 'expanded' : ''}>
        <FilterAccordionSummary 
          className={isExpanded ? 'expanded' : ''}
          onClick={onToggle}
        >
          <Typography className="section-title" variant="subtitle1" fontWeight="medium">
            {isMobile && selectedOptionName ? (
              <>
                <span style={{ color: '#919192' }}>{groupName}</span>
                <span style={{ color: '#000000', fontWeight: 400 }}> : {selectedOptionName}</span>
              </>
            ) : (
              groupName
            )}
          </Typography>
          <ExpandMoreIcon className="expand-icon" />
        </FilterAccordionSummary>
        {isExpanded && (
          <FilterAccordionDetails>
            {finalLayout === 'single' ? (
              <div>
                {optionsLeft.map((option) => (
                  <GenericButton
                    key={option.id}
                    className={selectedValue === option.id.toString() ? 'selected' : ''}
                    onClick={() => handleOptionSelect(option)}
                  >
                    {getLocalizedName(option, lang)}
                  </GenericButton>
                ))}
              </div>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  {optionsLeft.map((option) => (
                    <GenericButton
                      key={option.id}
                      className={selectedValue === option.id.toString() ? 'selected' : ''}
                      onClick={() => handleOptionSelect(option)}
                    >
                      {getLocalizedName(option, lang)}
                    </GenericButton>
                  ))}
                </Grid>
                <Grid item xs={6}>
                  {optionsRight.map((option) => (
                    <GenericButton
                      key={option.id}
                      className={selectedValue === option.id.toString() ? 'selected' : ''}
                      onClick={() => handleOptionSelect(option)}
                    >
                      {getLocalizedName(option, lang)}
                    </GenericButton>
                  ))}
                </Grid>
              </Grid>
            )}
          </FilterAccordionDetails>
        )}
      </FilterAccordion>
      <Divider />
    </>
  );
}; 